/**
 * Single source of truth for the default map viewport (per region/workspace).
 *
 * The app supports multiple regions (workspaces) — e.g. South Florida and
 * Central Florida. Each has its own bounding box + center. Callers pass the
 * active region id (or the `region` field off any user/pharmacy/order) into
 * `withMinMapBounds()` and `regionCenter()` to get the correct viewport.
 *
 * The bounding box guarantees a minimum coverage so single-stop routes don't
 * zoom in past the city; every additional point widens the box further.
 */
import L from "leaflet";

const BOUNDS = {
  // South Florida — Miami-Dade + Broward counties only (Deerfield Beach N →
  // Homestead S, Everglades W → Atlantic coast E). Palm Beach / Boca are
  // deliberately excluded so the map stays centered on the operator's real
  // service area.
  south_fl: [
    [26.32, -80.55], // NW — top of Broward (Deerfield Beach), west edge
    [25.14, -80.05], // SE — bottom of Miami-Dade (Florida City), Atlantic
  ],
  // Central Florida — Sanford/Deltona (N) → Kissimmee/St Cloud (S), Winter Garden (W) → Cocoa Beach (E)
  central_fl: [
    [28.90, -81.75], // NW
    [28.15, -80.60], // SE
  ],
};

const CENTERS = {
  // Miami-Dade + Broward midpoint (roughly Aventura / Hallandale area)
  south_fl: [25.95, -80.20],
  central_fl: [28.54, -81.38],
};

const DEFAULT_REGION = "south_fl";

function normalizeRegion(id) {
  return id && BOUNDS[id] ? id : DEFAULT_REGION;
}

export const SOUTH_FLORIDA_BOUNDS_CORNERS = BOUNDS.south_fl;   // kept for legacy imports
export const SOUTH_FLORIDA_CENTER = CENTERS.south_fl;          // kept for legacy imports

export function regionBoundsCorners(regionId) {
  return BOUNDS[normalizeRegion(regionId)];
}

export function regionCenter(regionId) {
  return CENTERS[normalizeRegion(regionId)];
}

/**
 * Returns a LatLngBounds covering at minimum the given region's default box
 * plus every additional point passed in. Always returns a valid bounds.
 */
export function withMinMapBounds(extraPoints = [], regionId = DEFAULT_REGION) {
  const b = L.latLngBounds(regionBoundsCorners(regionId));
  for (const p of extraPoints) {
    if (!p) continue;
    const lat = Array.isArray(p) ? p[0] : p.lat;
    const lng = Array.isArray(p) ? p[1] : p.lng;
    if (typeof lat === "number" && typeof lng === "number") {
      b.extend([lat, lng]);
    }
  }
  return b;
}

/**
 * Returns a LatLngBounds that tightly wraps the given points — north/south/
 * east/west extremes of the input only, no regional baseline. Useful for
 * planning maps where the operator wants the map to zoom in to the actual
 * cluster of stops instead of always covering the whole workspace region.
 *
 * If the input is empty, falls back to the region center (as a 1-point box
 * that Leaflet can still fitBounds on; caller supplies zoom fallback).
 * If the input has a single point, expands by a small radius so the map
 * doesn't zoom in past the city block level.
 */
export function boundsForPoints(points = [], regionId = DEFAULT_REGION) {
  const valid = [];
  for (const p of points) {
    if (!p) continue;
    const lat = Array.isArray(p) ? p[0] : p.lat;
    const lng = Array.isArray(p) ? p[1] : p.lng;
    if (typeof lat === "number" && typeof lng === "number") valid.push([lat, lng]);
  }
  if (valid.length === 0) {
    const [clat, clng] = regionCenter(regionId);
    // 0.5° x 0.5° box around region center so fitBounds has something to
    // work with when nothing is geocoded yet.
    return L.latLngBounds([clat - 0.25, clng - 0.25], [clat + 0.25, clng + 0.25]);
  }
  const b = L.latLngBounds(valid);
  if (valid.length === 1) {
    // Widen a single-point box by ~5 km so we don't zoom in past street level.
    b.extend([valid[0][0] + 0.05, valid[0][1] + 0.05]);
    b.extend([valid[0][0] - 0.05, valid[0][1] - 0.05]);
  }
  return b;
}
